import { request } from 'graphql-request';
import { Fetcher } from 'swr';
import useSWRImmutable from 'swr/immutable';

import { getLocationId } from '@/components/ClinicLocationsDropdown/getLocationOptions';
import { DEFAULT_LOCATION, DEFAULT_SKU } from '@/constants';
import config from '@/core/config/config';
import { LOCATIONS } from '@/graphql/queries/apps/locations';
import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { Location, Sku } from '@/types';
import { mapValidSkus } from '@/utils/skutype';
import { useCallback, useEffect, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useShallow } from 'zustand/react/shallow';
import { useGlobalLoading } from './useGlobalLoading';
import { useSlots } from './useSlots';
import { useUserLocation } from './useUserLocation';
import { handleDefaultSku } from '@/utils/sku370';

export const locationFetcher: Fetcher<{ locations: Location[] }, string> = (query) =>
  request(config.APPS_SERVER, query);

export const useLocationsQuery = () => {
  const { data, error, isLoading } = useSWRImmutable(LOCATIONS, { fetcher: locationFetcher });

  return {
    locationsData: data?.locations,
    locationsError: error,
    locationsLoading: isLoading,
  };
};

export const useLocations = () => {
  const { locationsData, locationsError, locationsLoading } = useLocationsQuery();
  const { data: userLocationData, isLoading: isUserLocationLoading } = useUserLocation();
  const [cookies, setCookie] = useCookies();

  const {
    location,
    locations,
    setErrorModal,
    setLocation,
    setLocations,
    setSku,
    session,
    selectedSku,
  } = usePrenuvoStore(
    useShallow((state) => ({
      location: state.scan.location,
      locations: state.scan.locations,
      setErrorModal: state.ui.methods.setErrorModal,
      setLocation: state.scan.methods.setLocation,
      setLocations: state.scan.methods.setLocations,
      setSku: state.scan.methods.setSku,
      session: state.scan.session,
      selectedSku: state.scan.sku,
    })),
  );

  const { getSlots } = useSlots();

  const skus = useMemo(() => mapValidSkus(location?.skus as Sku[]), [location]);

  const findAndSetSku = useCallback(
    (skuId: number) => {
      const selectedSku = skus.find((sku) => Number(sku.skuID) === skuId);
      if (selectedSku) setSku(selectedSku);
      return selectedSku;
    },
    [setSku, skus],
  );

  const updateLocations = useCallback(() => {
    if (locationsData && locations.length === 0) {
      setLocations(locationsData);
    }
    if (locationsError) {
      setErrorModal('Something went wrong', 'Please try again later.');
    }
  }, [locationsData, locationsError, setErrorModal, setLocations, locations.length]);

  const updateSkuAndSkuId = useCallback(
    (skuId: number) => {
      return findAndSetSku(skuId);
    },
    [findAndSetSku],
  );

  const handleSkuChange = useCallback(
    (changeSkuId: number, callerContext?: string) => {
      const selectedSku = updateSkuAndSkuId(changeSkuId);
      if (selectedSku)
        getSlots(undefined, { skuId: +changeSkuId, callerContext: callerContext || 'skuChange' });
    },
    [getSlots, updateSkuAndSkuId],
  );

  const handleLocationChange = useCallback(
    (changeLocationId: string) => {
      const location = getLocationId(changeLocationId, locationsData);
      if (location) {
        setCookie('PRENUVO_LOCATION', location.code, { path: '/' });
        setLocation(location);
        const defaultSku = handleDefaultSku(location?.code!, location?.skus);
        getSlots(undefined, {
          skuId: Number(defaultSku?.skuID),
          locationId: location.code,
          callerContext: 'handleLocationChange',
        });
      }
    },
    [getSlots, locationsData, setCookie, setLocation],
  );

  const setDefaultSelectedLocation = useCallback(() => {
    const { PRENUVO_LOCATION } = cookies;

    if (PRENUVO_LOCATION) {
      handleLocationChange(PRENUVO_LOCATION);
    } else if (!isUserLocationLoading && userLocationData?.locationId) {
      handleLocationChange(userLocationData.locationId);
    } else {
      handleLocationChange(DEFAULT_LOCATION);
    }
  }, [cookies, handleLocationChange, isUserLocationLoading, userLocationData]);

  useEffect(() => {
    updateLocations();
  }, [locationsLoading, updateLocations]);

  useEffect(() => {
    if (!location) {
      setDefaultSelectedLocation();
    } else if (!selectedSku) {
      updateSkuAndSkuId(DEFAULT_SKU);
    }
  }, [
    isUserLocationLoading,
    handleLocationChange,
    setDefaultSelectedLocation,
    location,
    selectedSku,
    updateSkuAndSkuId,
  ]);

  useEffect(() => {
    if (!session?.skuId) return;
    findAndSetSku(session?.skuId);
  }, [findAndSetSku, session?.skuId, setSku, skus]);

  useGlobalLoading(locationsLoading, true);

  return { handleLocationChange, handleSkuChange, skus };
};
